import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../components/AuthProvider';
import {
  archivarCampania, createCampaign, listCampaigns, listCampaignTypes, updateCampaign,
} from '../../api/campaigns';
import type { CampaignTypeRow } from '../../lib/db-types';
import type { CampaignFormInput } from '../../domain/campaignSchema';
import type { CampaignRow } from '../../lib/db-types';
import { puedeEditar } from '../../domain/permissions';
import CampaniaDatosForm, { EMPTY_CAMPAIGN_FORM, type CampaignFormValues } from './CampaniaDatosForm';
import FilaTarjeta from '../../components/FilaTarjeta';

const STATUS_LABEL: Record<string, string> = { borrador: 'Borrador', activa: 'Activa', cerrada: 'Cerrada' };
/**
 * Etiqueta del tipo. Sale de la maestra `campaign_types`; el mapa cableado que había antes solo
 * conocía tres de los nueve tipos reales, así que "mercaderismo" se pintaba crudo y en minúscula.
 *
 * El respaldo capitaliza el código en vez de mostrarlo tal cual: si algún día se agrega un tipo
 * y la maestra no llegó a cargarse, que se lea "Mercaderismo" y no "mercaderismo".
 */
function etiquetaTipo(code: string, tipos: CampaignTypeRow[]): string {
  const t = tipos.find((x) => x.code === code);
  if (t) return t.name;
  return code.charAt(0).toUpperCase() + code.slice(1).replace(/_/g, ' ');
}

/** Fila de la base → valores del formulario. Mismo mapeo que usa la ficha de la campaña. */
function aValoresDeFormulario(c: CampaignRow): CampaignFormValues {
  return {
    name: c.name ?? '',
    analyticAccount: c.analytic_account ?? '',
    client: c.client,
    brand: c.brand,
    type: c.type,
    description: c.description ?? '',
    quota: c.quota === null ? '' : String(c.quota),
    startDate: c.start_date,
    endDate: c.end_date,
    coordinatorId: c.coordinator_id ?? '',
    status: c.status as CampaignFormValues['status'],
  };
}

export default function Campanias() {
  const { user, roles } = useAuth();
  // El coordinador y el supervisor LEEN la ficha de sus campañas (fechas, horarios, tarifas:
  // es lo que tienen que cubrir). Crearlas es del comercial y del jefe — `campaigns_insert`
  // en la RLS exige rol comercial con `comercial_id = auth.uid()`, o admin.
  const puedeCrear = puedeEditar('campanias', roles);
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<CampaignRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  /** Campaña que se está editando desde la fila (CA3). */
  const [editando, setEditando] = useState<CampaignRow | null>(null);
  const [verArchivadas, setVerArchivadas] = useState(false);
  const [tipos, setTipos] = useState<CampaignTypeRow[]>([]);
  const [aviso, setAviso] = useState('');
  // Archivar es del jefe de operaciones: la RPC lo exige, así que el botón sigue esa regla.
  const esJefe = roles.includes('admin');

  /**
   * Por defecto solo lo VIGENTE este mes: no archivada, no banco de pruebas, y con el periodo
   * cruzando el mes en curso. Pedido del usuario del 1-sep — el listado mostraba 11 campañas
   * cuando solo 5 estaban vivas, y las 6 restantes habían terminado el 31 de agosto.
   *
   * La casilla sigue existiendo porque una campaña terminada todavía se consulta y se edita: si el
   * filtro fuera definitivo, cerrar agosto obligaría a des-archivar para ver su ficha.
   */
  const cargar = useCallback(() => {
    setLoading(true);
    listCampaigns({ incluirArchivadas: verArchivadas, soloVigentes: !verArchivadas })
      .then(setCampaigns)
      .catch((e) => setError(e instanceof Error ? e.message : 'Error al cargar campañas'))
      .finally(() => setLoading(false));
  }, [verArchivadas]);

  useEffect(cargar, [cargar]);

  // La maestra de tipos, para las etiquetas. Si falla no se corta la pantalla: `etiquetaTipo`
  // capitaliza el código y la lista se sigue leyendo.
  useEffect(() => { listCampaignTypes().then(setTipos).catch(() => setTipos([])); }, []);

  /**
   * Activa o desactiva. Reemplaza al borrado: borrar dejaba fotos huérfanas en el Storage —que
   * Supabase no deja borrar por SQL— y perdía el rastro de que la campaña existió.
   *
   * ⚠️ EN PANTALLA SE LLAMA "INACTIVA"; POR DENTRO SIGUE SIENDO `archived_at` (0027). No es un
   * descuido: el mecanismo estaba bien y la palabra estaba mal. El usuario lo dijo así el
   * 2026-08-15: *"cuando quiero ir ahí y cambiar, el estado solo me sale activo y archivado […]
   * si no tienes ninguna lógica asociada a ese campo, entonces que sea como que inactivo mejor,
   * para yo poder activar o desactivar alguna campaña"*. Tenía razón: "archivada" sugiere algo
   * histórico y definitivo, cuando lo que hace es un simple encendido/apagado reversible.
   *
   * NO se renombró la columna ni la RPC: sería una migración con riesgo sobre datos en producción
   * para no cambiar ningún comportamiento. Lo que el usuario ve es lo que importa acá.
   *
   * Ojo con el otro estado: `campaigns.status` (`borrador`/`activa`/`cerrada`) es una cosa
   * distinta y sigue existiendo. Activo/inactivo es visibilidad; `status` es la etapa.
   */
  async function alternarArchivo(c: CampaignRow): Promise<void> {
    setError(null);
    setAviso('');
    const archivar = c.archived_at === null;
    try {
      const vivas = await archivarCampania(c.id, archivar);
      if (!archivar) {
        setAviso(`${c.client} — ${c.brand} quedó activa otra vez.`);
      } else if (vivas > 0) {
        // Archivar no cancela turnos: quien los tenga los va a seguir viendo en su celular.
        setAviso(
          `${c.client} — ${c.brand} quedó inactiva y ya no aparece en el panel. Ojo: tiene `
          + `${vivas} asignación(es) viva(s), y esas personas seguirán viendo su turno en el `
          + 'celular. Si no deben trabajarlo, retira las asignaciones.',
        );
      } else {
        setAviso(`${c.client} — ${c.brand} quedó inactiva. No se borró nada: se puede reactivar.`);
      }
      cargar();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cambiar el estado de la campaña.');
    }
  }

  /**
   * Editar desde la LISTA, sin abrir la campaña (CA3). Reusa el mismo formulario y el mismo
   * `updateCampaign` que la ficha: dos formularios para lo mismo se desincronizan, y el error de
   * §3.22 —una lista de tipos cableada que rompía el guardado— nació exactamente de eso.
   */
  async function onEditar(parsed: CampaignFormInput) {
    if (!editando) return;
    await updateCampaign(editando.id, {
      name: parsed.name === '' ? null : parsed.name,
      analytic_account: parsed.analyticAccount === '' ? null : parsed.analyticAccount,
      client: parsed.client,
      brand: parsed.brand,
      type: parsed.type,
      description: parsed.description === '' ? null : parsed.description,
      quota: parsed.quota,
      start_date: parsed.startDate,
      end_date: parsed.endDate,
      coordinator_id: parsed.coordinatorId,
      status: parsed.status,
    });
    setAviso(`${parsed.client} — ${parsed.brand} actualizada.`);
    setEditando(null);
    cargar();
  }

  async function onCreate(parsed: CampaignFormInput) {
    const created = await createCampaign({
      name: parsed.name === '' ? null : parsed.name,
      analytic_account: parsed.analyticAccount === '' ? null : parsed.analyticAccount,
      client: parsed.client,
      brand: parsed.brand,
      type: parsed.type,
      description: parsed.description === '' ? null : parsed.description,
      quota: parsed.quota,
      start_date: parsed.startDate,
      end_date: parsed.endDate,
      comercial_id: user?.id ?? null, // autoasignado al creador
      coordinator_id: parsed.coordinatorId,
      status: parsed.status,
    });
    navigate(`/panel/campanias/${created.id}`);
  }

  return (
    <div className="p-4 max-w-5xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <h1 className="text-xl font-bold">Campañas</h1>
        <div className="flex flex-wrap items-center gap-3">
          {esJefe && (
            <label className="flex min-h-[44px] items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={verArchivadas}
                onChange={(e) => setVerArchivadas(e.target.checked)}
              />
              Ver todas (terminadas y archivadas)
            </label>
          )}
          {puedeCrear && (
            <button className="px-3 py-2 bg-blue-600 text-white rounded" onClick={() => setCreating(true)}>
              Nueva campaña
            </button>
          )}
        </div>
      </div>
      {error && <p className="text-red-600 mb-2">{error}</p>}
      {aviso && (
        <p className="mb-3 rounded border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">{aviso}</p>
      )}
      {/* En celular: una tarjeta por campaña (sin desplazamiento horizontal). */}
      <div className="space-y-2 lg:hidden">
        {loading && <p className="border rounded p-4 text-center text-gray-500">Cargando…</p>}
        {!loading && campaigns.length === 0 && (
          <p className="border rounded p-4 text-center text-gray-500">Sin campañas todavía</p>
        )}
        {campaigns.map((c) => (
          <FilaTarjeta
            key={c.id}
            titulo={c.name ?? `${c.client} — ${c.brand}`}
            {...(c.name ? { subtitulo: `${c.client} — ${c.brand}` } : {})}
            estados={
              <span className={
                c.status === 'activa' ? 'text-green-700'
                  : c.status === 'cerrada' ? 'text-gray-500' : 'text-yellow-700'
              }>
                {STATUS_LABEL[c.status] ?? c.status}
              </span>
            }
            subtitulo={c.archived_at !== null ? 'Inactiva' : undefined}
            campos={[
              { etiqueta: 'Tipo', valor: etiquetaTipo(c.type, tipos) },
              { etiqueta: 'Inicio', valor: c.start_date },
              { etiqueta: 'Fin', valor: c.end_date },
            ]}
            acciones={
              <>
                <Link className="text-blue-600 underline" to={`/panel/campanias/${c.id}`}>Abrir</Link>
                {puedeCrear && (
                  <button onClick={() => setEditando(c)} className="text-blue-600 underline">
                    Editar
                  </button>
                )}
                {esJefe && (
                  <button
                    onClick={() => void alternarArchivo(c)}
                    className="text-gray-600 underline"
                  >
                    {c.archived_at === null ? 'Desactivar' : 'Activar'}
                  </button>
                )}
              </>
            }
          />
        ))}
      </div>

      <div className="hidden overflow-x-auto lg:block">
        <table className="w-full text-sm border">
          <thead>
            <tr className="bg-gray-100 text-left">
              <th className="p-2">Cliente</th><th className="p-2">Marca</th><th className="p-2">Tipo</th>
              <th className="p-2">Inicio</th><th className="p-2">Fin</th><th className="p-2">Estado</th>
              <th className="p-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td className="p-2" colSpan={7}>Cargando…</td></tr>}
            {!loading && campaigns.length === 0 && (
              <tr><td className="p-2" colSpan={7}>Sin campañas todavía</td></tr>
            )}
            {campaigns.map((c) => (
              <tr key={c.id} className="border-t">
                <td className="p-2">
                  {c.name ?? c.client}
                  {c.name && <div className="text-xs text-gray-500">{c.client}</div>}
                </td>
                <td className="p-2">{c.brand}</td>
                <td className="p-2">{etiquetaTipo(c.type, tipos)}</td>
                <td className="p-2">{c.start_date}</td>
                <td className="p-2">{c.end_date}</td>
                <td className="p-2">
                  <span className={
                    c.status === 'activa' ? 'text-green-700'
                      : c.status === 'cerrada' ? 'text-gray-500' : 'text-yellow-700'
                  }>
                    {STATUS_LABEL[c.status] ?? c.status}
                  </span>
                  {c.archived_at !== null && (
                    <span className="ml-1 rounded bg-gray-200 px-2 py-0.5 text-xs text-gray-700">
                      inactiva
                    </span>
                  )}
                </td>
                <td className="p-2">
                  <Link className="text-blue-600 underline" to={`/panel/campanias/${c.id}`}>Abrir</Link>
                  {puedeCrear && (
                    <button onClick={() => setEditando(c)} className="ml-3 text-blue-600 underline">
                      Editar
                    </button>
                  )}
                  {esJefe && (
                    <button
                      onClick={() => void alternarArchivo(c)}
                      className="ml-3 text-gray-600 underline"
                    >
                      {c.archived_at === null ? 'Desactivar' : 'Activar'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editando && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded p-4 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold">
                Editar {editando.name ?? `${editando.client} — ${editando.brand}`}
              </h2>
              <button className="px-3 py-1 bg-gray-200 rounded" onClick={() => setEditando(null)}>
                Cancelar
              </button>
            </div>
            <CampaniaDatosForm
              initial={aValoresDeFormulario(editando)}
              submitLabel="Guardar cambios"
              onSubmit={onEditar}
            />
          </div>
        </div>
      )}

      {creating && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded p-4 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold">Nueva campaña</h2>
              <button className="px-3 py-1 bg-gray-200 rounded" onClick={() => setCreating(false)}>
                Cancelar
              </button>
            </div>
            {/* Compacto: solo lo mínimo para crear el asiento. El resto queda detrás de "Más
                datos" y con valores por defecto razonables — la programación se carga después,
                adentro, con la plantilla de horarios. */}
            <CampaniaDatosForm
              initial={EMPTY_CAMPAIGN_FORM}
              submitLabel="Crear campaña"
              compactoPorDefecto
              onSubmit={onCreate}
            />
          </div>
        </div>
      )}
    </div>
  );
}
